"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import Image from "next/image"
import React, { useCallback, useEffect, useState } from "react"

import Button, { ButtonProps } from "../Button"
import PageGrid from "../PageGrid"
import Quote from "../Quote"

export type QuoteItem = {
  quote: string
  name?: string
  title?: string
  location?: string
  quoteeImage?: {
    url: string
    title: string
    width?: number
    height?: number
  }
  companyImage?: {
    url: string
    title: string
    width?: number
    height?: number
  }
  caseStudyLink?: boolean
}

export type QuoteCarouselProps = {
  // Data
  quoteItems: QuoteItem[]

  // Layout & Styling
  wrapperClass?: string
  carouselProviderClass?: string
  textColorOverrideClass?: string

  // Homepage specific (quote marks)
  showQuoteMarks?: boolean

  // Navigation
  showDots?: boolean
  showArrows?: boolean

  // Grid Layout (for careers/customers - image + content layout)
  useGridLayout?: boolean
  showQuotee?: boolean

  // CTA Button
  buttonData?: React.ReactNode
  callToAction?: Omit<ButtonProps, "variant">

  // Carousel behavior
  loop?: boolean
  align?: "start" | "center" | "end"
}

const QuoteCarousel: React.FC<QuoteCarouselProps> = ({
  quoteItems,
  wrapperClass,
  carouselProviderClass,
  textColorOverrideClass,
  showQuoteMarks = false,
  showDots = true,
  showArrows = false,
  useGridLayout = false,
  buttonData,
  callToAction,
  loop = false,
  align = "center",
  showQuotee = false,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    align,
    skipSnaps: false,
    dragFree: false,
    duration: 20,
    slidesToScroll: 1,
  })

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  )

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onInit = useCallback((api: any) => {
    if (api) {
      setScrollSnaps(api.scrollSnapList())
    }
  }, [])

  const onSelect = useCallback((api: any) => {
    if (api) {
      setSelectedIndex(api.selectedScrollSnap())
      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onInit, onSelect])

  if (!quoteItems || quoteItems.length === 0) return null

  // For single quote display (no carousel)
  if (quoteItems.length === 1 && useGridLayout) {
    const data = quoteItems[0]
    return (
      <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4 md:gap-x-8 lg:gap-x-10 xl:gap-x-12 lg:grid-cols-12 xl:max-w-layout-xl lg:mx-auto lg:max-w-layout-lg">
        <div className="p-4 col-span-full lg:col-span-4">
          {data.quoteeImage && (
            <Image
              src={data.quoteeImage.url}
              alt={data.quoteeImage.title}
              width={235}
              height={235}
            />
          )}
        </div>
        <div className="p-4 col-span-full lg:col-span-8">
          <Quote
            quote={data.quote}
            name={data.name}
            caseStudyLink={data.caseStudyLink || false}
            quoteeDescription={data.title}
            textColorOverrideClass={textColorOverrideClass}
            location={data.location}
            showQuotee={showQuotee}
          />
        </div>
      </div>
    )
  }

  return (
    <section className={clsx("py-10 xl:py-28 lg:py-16 md:py-14", wrapperClass)}>
      {/* Homepage layout - matches legacy structure exactly with Embla */}
      {showQuoteMarks && (
        <>
          <div className="overflow-hidden">
            <div className="embla" ref={emblaRef}>
              <div className="embla__container">
                {quoteItems.map((item, index) => (
                  <div
                    key={item.name || `quote-${index}`}
                    className="embla__slide flex-[0_0_100%] min-w-0"
                  >
                    <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4 md:gap-x-8 lg:gap-x-10 xl:gap-x-12 lg:grid-cols-12 xl:max-w-layout-xl lg:mx-auto lg:max-w-layout-lg">
                      <div className="p-4 mb-8 md:mb-10 lg:mb-0 lg:col-span-4 col-span-full">
                        {item.companyImage && (
                          <Image
                            src={item.companyImage.url}
                            alt={item.title || "Company"}
                            width={item.companyImage.width || 200}
                            height={item.companyImage.height || 60}
                            className="xl:h-[52px] lg:h-[46px] h-[40px] object-contain object-left lg:object-center"
                          />
                        )}
                      </div>
                      <div className="p-4 lg:col-span-8 col-span-full">
                        <Image
                          sizes="(min-width: 56px) 56px, 100vw"
                          width={23}
                          height={23}
                          src="/homepage/quote-marks@2x.png"
                          alt="Decorative Quotation Marks"
                          className="h-[23px] w-[23px] mb-8 object-contain object-center"
                        />
                        <p className="mb-8 body1 text-gray-90 xl:mb-12 md:mb-10">
                          {item.quote}
                        </p>
                        <div className="flex items-center">
                          {item.quoteeImage && (
                            <Image
                              src={item.quoteeImage.url}
                              alt={item.title || "Person"}
                              width={200}
                              height={200}
                              className="xl:h-[36px] lg:h-[28px] h-[24px] object-contain object-left lg:object-center"
                            />
                          )}
                          <div>
                            <p className="uppercase caption2 text-gray-90">
                              {item.name}
                              <br />
                              {item.title}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <PageGrid>
            <div className="p-4 col-span-full lg:col-start-5 lg:col-span-8">
              <div className="flex items-center mb-10 -ml-2">
                {scrollSnaps.map((snap, index) => (
                  <button
                    key={`dot-${snap}`}
                    onClick={() => scrollTo(index)}
                    className="p-4"
                    type="button"
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    <div
                      className={clsx(
                        "rounded-full mr-8",
                        selectedIndex === index
                          ? "rounded-full bg-blue-50 w-3 h-3"
                          : "w-2 h-2 bg-gray-60",
                      )}
                    />
                  </button>
                ))}
              </div>
              <Button
                to="/customer-case-study"
                theme="light"
                variant="tertiary"
              >
                Hear from more customers
              </Button>
            </div>
          </PageGrid>
        </>
      )}

      {/* Non-homepage layouts */}
      {!showQuoteMarks && (
        <PageGrid isGrid={!useGridLayout}>
          <div className={clsx("w-full", carouselProviderClass)}>
            {/* Embla viewport */}
            <div className="embla" ref={emblaRef}>
              <div className="embla__container">
                {quoteItems.map((item) => (
                  <div
                    key={`quote-${item.quote.slice(0, 50).replace(/\s/g, "-")}`}
                    className="embla__slide"
                  >
                    {/* Grid layout style - for careers/customers */}
                    {useGridLayout && (
                      <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4 md:gap-x-8 lg:gap-x-10 xl:gap-x-12 lg:grid-cols-12 xl:max-w-layout-xl lg:mx-auto lg:max-w-layout-lg">
                        <div className="p-4 col-span-full lg:col-span-4">
                          {/* Show quoteeImage for careers (when person photo exists), companyImage for customers */}
                          {(() => {
                            if (item.companyImage) {
                              return (
                                <Image
                                  src={item.companyImage.url}
                                  alt={item.companyImage.title || "Company"}
                                  width={235}
                                  height={120}
                                />
                              )
                            }
                            if (item.quoteeImage) {
                              return (
                                <Image
                                  src={item.quoteeImage.url}
                                  alt={item.quoteeImage.title || "Person"}
                                  width={235}
                                  height={235}
                                  className="rounded-2xl"
                                />
                              )
                            }

                            return null
                          })()}
                        </div>
                        <div className="p-4 col-span-full lg:col-span-8">
                          <Quote
                            quoteeImage={item.quoteeImage}
                            quote={item.quote}
                            name={item.name}
                            caseStudyLink={item.caseStudyLink || false}
                            quoteeDescription={item.title}
                            textColorOverrideClass={textColorOverrideClass}
                            location={item.location}
                            showQuotee={showQuotee}
                          />
                        </div>
                      </div>
                    )}

                    {/* Simple layout - for other cases */}
                    {!useGridLayout && (
                      <div className="text-center px-4">
                        <blockquote className="text-xl md:text-2xl lg:text-3xl mb-6">
                          {item.quote}
                        </blockquote>
                        <div className="flex items-center justify-center space-x-4">
                          <div>
                            <div className="font-semibold">{item.name}</div>
                            <div className="text-gray-60">{item.title}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Controls for non-homepage */}
            {quoteItems.length > 1 && (
              <>
                {/* Dots Navigation */}
                {showDots &&
                  scrollSnaps.length > 1 &&
                  (() => {
                    let firstDivClassName =
                      "col-span-full flex justify-center mt-8"
                    let secondDivClassName = "col-span-full flex justify-center"

                    if (useGridLayout) {
                      firstDivClassName = "col-span-full lg:col-span-4"
                      secondDivClassName = "p-4 col-span-full lg:col-span-8"
                    }

                    return (
                      <PageGrid className={useGridLayout ? "mx-0" : ""}>
                        <div className={firstDivClassName} />
                        <div className={secondDivClassName}>
                          <div className="flex items-center mb-10 -ml-3">
                            {scrollSnaps.map((snap, index) => (
                              <button
                                key={`slide-dot-${snap}`}
                                onClick={() => scrollTo(index)}
                                className="p-4"
                                type="button"
                                aria-label={`Go to slide ${index + 1}`}
                              >
                                <div
                                  className={clsx(
                                    "rounded-full mr-8",
                                    selectedIndex === index
                                      ? "bg-blue-50 w-3 h-3"
                                      : "w-2 h-2 bg-gray-60",
                                  )}
                                />
                              </button>
                            ))}
                          </div>
                          {/* Button integrated into same grid layout for useGridLayout */}
                          {useGridLayout && (callToAction || buttonData) && (
                            <div>
                              {callToAction ? (
                                <Button {...callToAction} />
                              ) : (
                                buttonData
                              )}
                            </div>
                          )}
                        </div>
                      </PageGrid>
                    )
                  })()}

                {/* Arrow Navigation */}
                {showArrows && (
                  <div className="flex justify-center space-x-4 mt-6">
                    <button
                      onClick={scrollPrev}
                      disabled={!canScrollPrev}
                      className="p-2 rounded-full border border-gray-30 disabled:opacity-50"
                      type="button"
                      aria-label="Previous slide"
                    >
                      ←
                    </button>
                    <button
                      onClick={scrollNext}
                      disabled={!canScrollNext}
                      className="p-2 rounded-full border border-gray-30 disabled:opacity-50"
                      type="button"
                      aria-label="Next slide"
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Call to Action for non-grid layouts only */}
            {!useGridLayout && (callToAction || buttonData) && (
              <div className="flex justify-center mt-8">
                {callToAction ? <Button {...callToAction} /> : buttonData}
              </div>
            )}
          </div>
        </PageGrid>
      )}
    </section>
  )
}

export default QuoteCarousel
